// The pool as a shadow-play screen: a light box behind paper. Lit pens are
// single gels (the colour of the crab that closed them) in thick black came;
// the undertow is black paper sliding over the light. Shapes come from masks
// blurred on the CPU and read back with hardware bilinear filtering, so every
// edge is a smooth curve rather than the sim's cell grid.
import * as THREE from 'three';
import { nightGelHex } from './gels.js';
export { blurMasks } from './gels.js';

export const KAGEE_WATER_FS = /* glsl */`
uniform sampler2D cells; uniform sampler2D field; uniform sampler2D gelA; uniform sampler2D gelB; uniform sampler2D mask;
uniform vec2 gridSize; uniform vec4 gridBox; uniform vec2 origin; uniform float now;
uniform vec3 gels[8]; uniform vec3 screenA; uniform vec3 screenB; uniform float mode;
uniform vec4 ripples[12]; uniform vec2 flow; uniform float pattern; uniform float calm;
uniform sampler2D surf; uniform vec2 surfTexel;   // the moving surface (height field, 128 = rest)
varying vec3 vWorld;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y); }
vec4 cellAt(vec2 c) { return texture2D(cells, (c + 0.5) / gridSize); }
// A line of constant screen width along the 0.5 contour of a smooth field.
float contour(float v, float px) { return 1.0 - smoothstep(0.0, px * fwidth(v), abs(v - 0.5)); }

void main() {
  vec2 wp = vWorld.xz;
  vec2 lp = wp - origin;
  vec2 uv = (lp - gridBox.xy) / gridBox.zw;
  if (texture2D(mask, uv).r < 0.5) discard;
  vec2 g = uv * gridSize - 0.5;
  vec2 c0 = floor(g);
  // The surface: slope and curvature of the height field. What lies under the
  // water (the gels, the came, the undertow) is seen through it, bent by the slope.
  float hC = texture2D(surf, uv).r, hL = texture2D(surf, uv - vec2(surfTexel.x, 0.0)).r, hR = texture2D(surf, uv + vec2(surfTexel.x, 0.0)).r;
  float hD = texture2D(surf, uv - vec2(0.0, surfTexel.y)).r, hU = texture2D(surf, uv + vec2(0.0, surfTexel.y)).r;
  vec2 grad = vec2(hR - hL, hU - hD);          // in byte units, about 0.004 per unit of height difference
  float lap = (hL + hR + hU + hD - 4.0 * hC) * 2.0;
  vec2 ruv = uv + grad * 0.16;
  vec4 F = texture2D(field, ruv);          // r lit, g dark, b picture, a forecast (all smoothed)

  // The screen: light behind washi. Long fibres, a warm-to-cool falloff.
  vec2 rel = uv - 0.5;
  float fall = clamp(1.15 - dot(rel, rel) * 2.4, 0.0, 1.0);
  float fibre = noise(wp * vec2(9.0, 0.9)) * 0.6 + noise(wp * vec2(0.8, 8.0)) * 0.4;
  vec3 col = mix(screenA, screenB, fall) * (0.9 + 0.18 * fibre);
  // Star nights: the screen is a black mirror with the sky in it.
  if (mode > 0.5 && mode < 1.5) {
    // Round, unevenly bright points: the sky seen in black water.
    vec2 sp = floor(wp * 5.0), sf = fract(wp * 5.0) - 0.5;
    vec2 jit = vec2(hash(sp + 1.7), hash(sp + 9.2)) - 0.5;
    float big = hash(sp + 4.4);
    float s = step(0.9, hash(sp)) * smoothstep(0.035 + 0.05 * big, 0.0, length(sf - jit * 0.7));
    s *= 0.55 + 0.45 * sin(now * (1.0 + big * 2.0) + big * 40.0);
    col += vec3(0.85, 0.88, 1.0) * s * 0.9;
  }
  // The current, drawn as 流水文 (the flowing-water pattern of kimono and
  // lacquer): long sinuous lines in loose bundles of three, each a brush stroke
  // that swells and tapers, all drifting downstream. Moonlit silver, not white.
  if (dot(flow, flow) > 0.0) {
    vec2 fd = normalize(flow), fn = vec2(-fd.y, fd.x);
    float s = dot(wp, fd) - now * 0.45;                  // along the flow, drifting
    float across = dot(wp, fn);
    float bend = sin(s * 0.8 + across * 0.35) * 0.32 + sin(s * 0.31 - across * 0.17 + 1.7) * 0.22;
    float q = (across + bend) * 3.4;                     // about 3.4 lines per unit across
    float line = floor(q), bundle = floor(line / 3.0);
    float keep = step(0.6, hash(vec2(bundle, 7.0)));     // some bundles are left out: water between
    float life = noise(vec2(s * 0.32 + hash(vec2(line, 2.0)) * 9.0, line * 1.7));
    float brush = smoothstep(0.46, 0.78, life);          // a stroke's body; its ends taper to nothing
    float mid = 1.0 - abs(mod(line, 3.0) - 1.0) * 0.35;  // the middle line of a bundle reads strongest
    float px = fwidth(q);
    float halfW = px * (0.3 + 0.9 * brush * mid);     // half-width in line units, about 0.5 to 2 px
    float ink = 1.0 - smoothstep(halfW, halfW + px, abs(fract(q) - 0.5));
    col = mix(col, vec3(0.6, 0.64, 0.78), ink * brush * keep * 0.34);
  }

  // Reveal times from the four nearest cells (so edges are not left behind).
  float reveal = 0.0, darkIn = 0.0, front = 0.0;
  for (int k = 0; k < 4; k++) {
    vec4 t = cellAt(c0 + vec2(float(k - (k / 2) * 2), float(k / 2)));
    if (t.r >= 0.0) { reveal = max(reveal, smoothstep(t.r, t.r + 0.22, now)); front = max(front, exp(-max(now - t.r, 0.0) * 8.0) * step(t.r, now)); }
    if (t.g >= 0.0) darkIn = max(darkIn, smoothstep(t.g - 0.1, t.g + 0.6, now));
  }

  // Which gel: each gel has its own smoothed mask; the strongest wins, and
  // the came runs where the two strongest are level, at constant screen width.
  vec4 ga = texture2D(gelA, ruv), gb = texture2D(gelB, ruv);
  float m[8]; m[0] = ga.r; m[1] = ga.g; m[2] = ga.b; m[3] = ga.a; m[4] = gb.r; m[5] = gb.g; m[6] = gb.b; m[7] = gb.a;
  float idA = 0.0, mA = -1.0, mB = 0.0;
  for (int k = 0; k < 8; k++) {
    if (m[k] > mA) { mB = mA; mA = m[k]; idA = float(k); } else if (m[k] > mB) mB = m[k];
  }
  mB = max(mB, 0.0);
  float dm = mA - mB;
  float seam = (1.0 - smoothstep(2.2 * fwidth(dm), 3.2 * fwidth(dm) + 0.002, dm)) * step(0.06, mB);
  vec3 gel = gels[int(clamp(idA, 0.0, 7.0))];
  if (mode > 3.5) gel = mix(vec3(0.012, 0.016, 0.05), vec3(0.75, 0.42, 0.07), smoothstep(0.42, 0.58, F.b));   // picture: the motif lights gold, the rest stays night blue
  // Glass patterns (looks only).
  if (pattern > 0.5 && pattern < 1.5) { float f2 = abs(fract((wp.x + wp.y) * 2.5) - 0.5) + abs(fract((wp.x - wp.y) * 2.5) - 0.5); gel *= 0.8 + 0.45 * step(0.66, f2); }
  if (pattern > 1.5 && pattern < 2.5) { vec2 q = fract(wp * 1.6) - 0.5; gel = mix(gel, gel * 0.45 + vec3(0.45), smoothstep(0.06, 0.0, abs(length(q) - 0.28)) * 0.7); }
  if (pattern > 2.5) gel += vec3(0.8, 0.6, 0.2) * step(0.985, hash(floor(wp * 22.0))) * 0.8;
  float litShape = smoothstep(0.46, 0.54, F.r) * reveal;
  float mott = 0.82 + 0.3 * noise(wp * 2.1 + idA * 7.0) + 0.12 * noise(wp * vec2(8.0, 1.3));
  mott *= 0.5 + 0.5 * smoothstep(0.0, 0.35, min(dm, (F.r - 0.5) * 2.0));   // glass darkens toward its came
  mott *= 0.8 + 0.35 * fibre;                                                   // the washi shows through the gel
  col = mix(col, gel * mott, litShape);
  col += vec3(1.0, 0.95, 0.85) * front * litShape * 0.3;
  // Came: round every pen and between pieces, constant screen width.
  float came = max(contour(F.r, 4.5) * step(0.01, reveal), seam * litShape);
  col = mix(col, vec3(0.01, 0.008, 0.015), came * 0.95);

  // Picture nights: the motif drawn in gold on the paper, a faint ghost inside.
  if (mode > 3.5) {
    float inside = smoothstep(0.46, 0.54, F.b);
    col = mix(col, col * vec3(1.05, 0.95, 0.8), inside * (1.0 - litShape) * 0.35);
    // The cut line still to light: dark and dashed, like a pattern on paper.
    float dash = step(0.45, fract((wp.x * 0.7 + wp.y) * 3.0));
    col = mix(col, vec3(0.03, 0.018, 0.006), contour(F.b, 2.2) * (1.0 - litShape) * mix(0.35, 0.95, dash));
  }

  // The undertow: black paper sliding in, its edge slightly torn.
  float torn = (noise(wp * 3.0 + now * 0.2) - 0.5) * 0.12;
  float dark = smoothstep(0.45, 0.55, F.g + torn) * darkIn;
  col = mix(col, vec3(0.004, 0.003, 0.008), dark);
  col += vec3(0.25, 0.12, 0.45) * contour(F.g + torn, 1.4) * darkIn * 0.5;
  // Next turn's dark: a hatch of shadow.
  // Violet stripes drifting across it, strong enough to read on a phone.
  float hatch = step(0.6, fract((wp.x + wp.y) * 2.2 - now * 0.35));
  float reach = smoothstep(0.4, 0.6, F.a) * (1.0 - dark);
  col = mix(col, col * 0.35, reach * 0.6);
  col = mix(col, vec3(0.32, 0.16, 0.5), reach * hatch * 0.75);

  // Light on the moving surface: crests gather it, troughs thin it, and wave
  // fronts facing the moon glint. Black paper (the undertow) shows none of it.
  float paper = 1.0 - dark;
  col *= 1.0 + clamp(-lap * 3.0, -0.12, 0.18) * paper;
  float glint = dot(grad, normalize(vec2(0.35, -1.0)));
  col += vec3(0.95, 0.92, 0.8) * smoothstep(0.025, 0.08, glint) * 0.26 * paper;   // thin bright edges, never a white wash
  col += screenB * calm * 0.3;
  gl_FragColor = vec4(col, 0.98);
}`;

// The night's gels as THREE colours (see gels.js for the rule).
export const nightGels = (phase, shellHex) => nightGelHex(phase, shellHex).map(h => new THREE.Color(h));

// The light behind the screen, per game of the night.
export const SCREENS = {
  pen: ['#05070f', '#161b34'],
  stars: ['#010208', '#070c22'],
  lantern: ['#140b03', '#4e2c0c'],
  floats: ['#020c12', '#082430'],
  picture: ['#1e1b16', '#5a5242'],
};
export const MODE_INDEX = { pen: 0, stars: 1, lantern: 2, floats: 3, picture: 4 };
